import type { LedgerStatusTone } from "@/features/clinic/types/billing";
import type {
  BillingPatientCounts,
  BillingPatientFilter,
  BillingPatientView,
} from "@/features/clinic/types/billing-patients";
import type { SortOption } from "@/features/common/types/sort";
import type {
  BillingSource,
  LedgerStatus,
  LedgerType,
} from "@/features/clinic/types/billing";

import { BILLING_PATIENT_VIEW } from "@/features/clinic/types/billing-patients";
import {
  BILLING_SOURCE,
  LEDGER_STATUS,
  LEDGER_TYPE,
} from "@/features/clinic/types/billing";

export const LEDGER_DEFAULT_PAGE_SIZE = 25;

export const BILLING_EMPTY_LABEL = "-";

export const BILLING_UNKNOWN_PATIENT = "Unknown patient";

/** The stock table look with every row pointing like a link. */
export const BILLING_GRID_CLASS =
  "stock-table--compact !h-full !min-h-0 [&_[role=row]]:cursor-[var(--cursor-interactive)]";

/** The one request behind the panel's view counts and the money line; the page and the panel share its cache. */
export const BILLING_COUNTS_FILTER: BillingPatientFilter = {
  page: 1,
  limit: 1,
  view: BILLING_PATIENT_VIEW.OWES,
};

/** The sentinel every "no filter" control uses - a Select cannot hold undefined. */
export const LEDGER_ANY = "any";

/** In ledger order: what was billed, what was taken, what was prepaid. */
export const LEDGER_TYPE_OPTIONS: SortOption<LedgerType | typeof LEDGER_ANY>[] =
  [
    { key: LEDGER_ANY, label: "All" },
    { key: LEDGER_TYPE.INVOICE, label: "Invoices" },
    { key: LEDGER_TYPE.RECEIPT, label: "Receipts" },
    { key: LEDGER_TYPE.ADVANCE, label: "Advances" },
  ];

/**
 * Statuses offered per record type. An invoice speaks one enum and the other two speak
 * another, so offering all six at once would list statuses that can never
 * match the rows on screen.
 */
export const LEDGER_STATUS_OPTIONS: Record<
  LedgerType | typeof LEDGER_ANY,
  SortOption<LedgerStatus>[]
> = {
  [LEDGER_ANY]: [
    { key: LEDGER_STATUS.PAID, label: "Paid" },
    { key: LEDGER_STATUS.CLEARED, label: "Cleared" },
    { key: LEDGER_STATUS.PENDING, label: "Pending" },
    { key: LEDGER_STATUS.UNPAID, label: "Unpaid" },
    { key: LEDGER_STATUS.DRAFT, label: "Draft" },
    { key: LEDGER_STATUS.VOID, label: "Void" },
  ],
  [LEDGER_TYPE.INVOICE]: [
    { key: LEDGER_STATUS.PAID, label: "Paid" },
    { key: LEDGER_STATUS.UNPAID, label: "Unpaid" },
    { key: LEDGER_STATUS.DRAFT, label: "Draft" },
    { key: LEDGER_STATUS.VOID, label: "Void" },
  ],
  [LEDGER_TYPE.RECEIPT]: [
    { key: LEDGER_STATUS.CLEARED, label: "Cleared" },
    { key: LEDGER_STATUS.PENDING, label: "Pending" },
    { key: LEDGER_STATUS.VOID, label: "Void" },
  ],
  [LEDGER_TYPE.ADVANCE]: [
    { key: LEDGER_STATUS.CLEARED, label: "Cleared" },
    { key: LEDGER_STATUS.PENDING, label: "Pending" },
    { key: LEDGER_STATUS.VOID, label: "Void" },
  ],
};

export const LEDGER_SOURCE_OPTIONS: SortOption<
  BillingSource | typeof LEDGER_ANY
>[] = [
  { key: LEDGER_ANY, label: "Any source" },
  { key: BILLING_SOURCE.CRM, label: "From the EMR" },
  { key: BILLING_SOURCE.MANUAL, label: "Added by hand" },
];

export const LEDGER_DATE_RANGE = {
  ANY: LEDGER_ANY,
  TODAY: "today",
  LAST_7: "last-7",
  LAST_30: "last-30",
  THIS_MONTH: "this-month",
  LAST_MONTH: "last-month",
} as const;

export type LedgerDateRange =
  (typeof LEDGER_DATE_RANGE)[keyof typeof LEDGER_DATE_RANGE];

export const LEDGER_DATE_RANGE_OPTIONS: SortOption<LedgerDateRange>[] = [
  { key: LEDGER_DATE_RANGE.ANY, label: "Any time" },
  { key: LEDGER_DATE_RANGE.TODAY, label: "Today" },
  { key: LEDGER_DATE_RANGE.LAST_7, label: "Last 7 days" },
  { key: LEDGER_DATE_RANGE.LAST_30, label: "Last 30 days" },
  { key: LEDGER_DATE_RANGE.THIS_MONTH, label: "This month" },
  { key: LEDGER_DATE_RANGE.LAST_MONTH, label: "Last month" },
];

/** The summary shelf, in the order money travels: billed, collected, prepaid. */
export const LEDGER_SUMMARY_CELLS = [
  {
    key: "invoiced",
    label: "Invoiced",
    hint: "Every invoice the filter matches, except the void ones and any held as a suspected duplicate.",
  },
  {
    key: "collected",
    label: "Collected",
    hint: "Cleared receipts - money actually taken, whether paid directly or drawn from an advance.",
  },
  {
    key: "advances",
    label: "Advances",
    hint: "Cleared prepayments. Spending one shows up under Collected, so the two overlap by design.",
  },
] as const;

/** How a manual advance was paid - a note for the ledger, never a rule. */
export const ADVANCE_PAYMENT_TYPES = ["Cash", "Card", "Transfer", "Other"];

/** How old what we hold for a visit may be before the outcome flow asks the CRM again unprompted. */
export const HELD_BILLING_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const LEDGER_TONE_TEXT_CLASS: Record<LedgerStatusTone, string> = {
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  default: "text-muted",
};

/** Debt this old is what someone opened the page to chase. */
export const STALE_DEBT_DAYS = 60;

/** The window the "collected" figure and its comparison are read over. */
export const COLLECTED_WINDOW_DAYS = 30;

/** The jobs somebody opens billing to do, worst-first, with the document ledger last. Rows under Billing in the Clinic panel. */
export const BILLING_PATIENT_VIEWS: Array<{
  key: BillingPatientView;
  label: string;
  countKey?: keyof BillingPatientCounts;
}> = [
  { key: BILLING_PATIENT_VIEW.OWES, label: "Owes money", countKey: "owes" },
  {
    key: BILLING_PATIENT_VIEW.REVIEW,
    label: "Needs review",
    countKey: "review",
  },
  {
    key: BILLING_PATIENT_VIEW.CREDIT,
    label: "Credit on account",
    countKey: "credit",
  },
  { key: BILLING_PATIENT_VIEW.ALL, label: "All patients", countKey: "all" },
  { key: BILLING_PATIENT_VIEW.DOCUMENTS, label: "Documents" },
];
